'use client';import { JSONWebToken, Theme } from '@parkingbase/ecommerce-api-test';


import { Provider as RollbarProvider } from '@rollbar/react';
import { AnalyticsProvider } from './AnalyticsProvider';
import { AppBootGate } from './AppBootGate';
import { AppDataProvider } from './AppDataProvider';
import { BasePathProvider } from './BasePathProvider';
import { ClientRouteGuardProvider } from './ClientRouteGuardProvider';
import { CookieConsentProvider } from './CookieConsentProvider';
import { HotjarProvider } from './HotjarProvider';
import { HydrateStoreProvider } from './HydrateStoreProvider';
import { IsolatedProvider, ProviderErrorBoundary } from './ProviderErrorBoundary';
import { SessionTimeoutWatcher } from '@/components/session-timeout';
import { ReCaptchaProvider } from './ReCaptchaProvider';
import { RollbarPersonSync } from './RollbarPersonSync';
import { SupportWidgetProvider } from './SupportWidgetProvider';
import { ThemeProvider } from './ThemeProvider';
import { CaptchaChallengeProvider } from '@/modules/captcha';
import { getRollbar, rollbarConfig } from '@/lib/rollbar';


export function RootStoreProvider({
  children,
  defaultTheme,
  tokenDetails,
  refreshFailed,
}: {
  children: React.ReactNode;
  defaultTheme: Theme | null;
  tokenDetails: JSONWebToken | null;
  refreshFailed: boolean;
}) {
  // Reuse the same Rollbar instance the logger forwards to. Without this,
  // <RollbarProvider config={...}> would create a SECOND instance with its own
  // captureUncaught/captureUnhandledRejections handlers, causing every unhandled
  // error to be reported twice. On SSR / dev / no-token, getRollbar() returns
  // null and we fall back to passing config — the Provider creates a no-op
  // instance there since rollbarConfig.enabled is false off the browser.
  const rollbarInstance = getRollbar();
  const rollbarProviderProps = rollbarInstance
    ? { instance: rollbarInstance }
    : { config: rollbarConfig };

  return (
    /* Rollbar provider as the outermost layer so the SDK is initialized
       before any downstream provider can throw. When NEXT_PUBLIC_ROLLBAR_ACCESS_TOKEN
       is unset (dev / tests), rollbarConfig.enabled is false and the provider
       is a near-zero-cost no-op. */
    <RollbarProvider {...rollbarProviderProps}>
      {/* Cookie consent banner — kept above the inner provider stack so it stays visible
          regardless of inner provider state.

          THIS IS THE ONLY PLACE THIS PROVIDER MAY BE MOUNTED. It renders a <CookieBanner />
          beside its children, so a second one anywhere in the tree paints an identical
          `fixed bottom-0 z-[9998]` copy directly over this one — the later copy wins the
          hit test and the banner's own buttons stop responding to clicks. app/layout.tsx
          wrapped RootStoreProvider in a second one for four months (PB-5605). */}
      <CookieConsentProvider>
        {/* Hydrate the store with the token details */}
        <HydrateStoreProvider tokenDetails={tokenDetails}>
          <RollbarPersonSync />
          {/* Base path context so navigation and links respect / vs /c/{companyId}. */}
          <BasePathProvider>
            {/* Google reCAPTCHA provider for payment operations */}
            <ReCaptchaProvider>
              {/* Bot-check puzzle shown whenever the API refuses a reCAPTCHA token. Must sit
                  INSIDE ReCaptchaProvider — it registers the token minter that the Axios
                  interceptor uses to re-mint after the customer solves. (PB-3899) */}
              <CaptchaChallengeProvider>
                {/* Client-side route guard: handles cookie cleanup + redirect on refresh failure */}
                <ClientRouteGuardProvider refreshFailed={refreshFailed}>
                  {/* Initialize the all API */}
                  <AppDataProvider>
                    {/* Apply the theme to the application */}
                    <ThemeProvider defaultTheme={defaultTheme ?? undefined}>
                      {/* Automatic sign-out on inactivity (PB-5476). Mounted ONCE here rather
                          than in (app)/layout and (public)/PublicLayoutClient: a signed-in
                          customer lives on public routes too, so the layout split does not map
                          onto session surfaces, and two mounts would run two clocks. Sits above
                          AppBootGate because that gate swaps children for a loader during boot,
                          which would remount the watcher and reset the clock. Isolated so a
                          session watchdog can never white-screen the app. */}
                      <ProviderErrorBoundary name="SessionTimeoutWatcher" fallback={null}>
                        <SessionTimeoutWatcher />
                      </ProviderErrorBoundary>
                      {/* Non-critical providers — wrap with IsolatedProvider so a vendor-script crash
                        is logged and the page still renders. To add another isolated provider,
                        nest one more <IsolatedProvider name="X" Provider={X}> inside. */}
                      <IsolatedProvider name="AnalyticsProvider" Provider={AnalyticsProvider}>
                        <IsolatedProvider name="HotjarProvider" Provider={HotjarProvider}>
                          <IsolatedProvider
                            name="SupportWidgetProvider"
                            Provider={SupportWidgetProvider}
                          >
                            {/* Hold a full-screen loader on every page until the
                                refresh-token exchange has resolved the auth state,
                                so no page flashes a guest UI before hydration. (PB-3288) */}
                            <AppBootGate>{children}</AppBootGate>
                          </IsolatedProvider>
                        </IsolatedProvider>
                      </IsolatedProvider>
                    </ThemeProvider>
                  </AppDataProvider>
                </ClientRouteGuardProvider>
              </CaptchaChallengeProvider>
            </ReCaptchaProvider>
          </BasePathProvider>
        </HydrateStoreProvider>
      </CookieConsentProvider>
    </RollbarProvider>
  );
}
